import { useState, useEffect } from 'react';
import { storage } from '../utils/storage';

// ----------------------------------------------------------------------

export default function useLocalStorage<ValueType>(key: string, defaultValue: ValueType) {
  const [value, setValue] = useState(() => {
    // Try to get from cache first, then fallback to localStorage for immediate access
    const storedValue = storage.getItemSync(key);
    return storedValue === null ? defaultValue : storedValue;
  });

  useEffect(() => {
    // Load initial value from IndexedDB
    const loadInitialValue = async () => {
      try {
        const storedValue = await storage.getItem(key);
        if (storedValue !== null) {
          setValue(storedValue);
        }
      } catch (error) {
        console.error(`Failed to load ${key} from storage:`, error);
      }
    };

    loadInitialValue();

    // Listen for localStorage changes (for cross-tab synchronization)
    const listener = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === key) {
        const newValue = e.newValue ? JSON.parse(e.newValue) : e.newValue;
        setValue(newValue);
      }
    };
    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [key, defaultValue]);

  const setValueInStorage = (newValue: ValueType) => {
    setValue((currentValue: any) => {
      const result = typeof newValue === 'function' ? newValue(currentValue) : newValue;

      // Use sync method for immediate update
      storage.setItemSync(key, result);

      return result;
    });
  };

  return [value, setValueInStorage];
}
